import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useNavigate } from "react-router-dom";
import { FaUser, FaEnvelope, FaPhone, FaEdit, FaSave, FaTimes, FaKey, FaPlus } from "react-icons/fa";
import { fetchUserProfile, updateUserProfile, getStages, getClassesByStage } from "../../redux/slices/authSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Profile.css";

// استيراد BASE_URL من authSlice
const BASE_URL = "https://newalemny.3almny.app/api";

function Profile() {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { 
    profileData, 
    profileLoading, 
    profileError,
    updateProfileLoading,
    updateProfileError,
    stages,
    classes,
    stagesLoading,
    classesLoading
  } = useSelector((state) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    stage_id: "",
    class_id: "",
    prof_img: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  
  // إضافة state لكود الابن
  const [childCode, setChildCode] = useState("");
  const [isAddingChild, setIsAddingChild] = useState(false);

  // Check authentication
  const isAuthenticated = !!localStorage.getItem("token3almny");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Fetch profile data
    dispatch(fetchUserProfile());
    
    // Fetch stages for dropdown only for students
    if (profileData?.role !== 'teacher') {
      dispatch(getStages());
    }
  }, [dispatch, isAuthenticated, navigate, profileData?.role]);

  // Update form data when profile data is loaded
  useEffect(() => {
    if (profileData) {
      setFormData({
        name: profileData.name || "",
        email: profileData.email || "",
        stage_id: profileData.stage_id || "",
        class_id: profileData.class_id || "",
        prof_img: null
      });
      setPreviewImage(profileData.prof_img);
    }
  }, [profileData]);

  // Fetch classes when stage changes (only for students)
  useEffect(() => {
    if (profileData?.role !== 'teacher' && formData.stage_id && stages.length > 0) {
      dispatch(getClassesByStage(formData.stage_id));
    }
  }, [formData.stage_id, stages, dispatch, profileData?.role]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        prof_img: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await dispatch(updateUserProfile(formData)).unwrap();
      setIsEditing(false);
      toast.success("تم تحديث البيانات الشخصية بنجاح");
    } catch (error) {
      // Display the specific error message from the API
      const errorMessage = error || "حدث خطأ في تحديث البيانات";
      toast.error(errorMessage);
      console.error("Profile update error:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (profileData) {
      setFormData({
        name: profileData.name || "",
        email: profileData.email || "",
        stage_id: profileData.stage_id || "",
        class_id: profileData.class_id || "",
        prof_img: null
      });
      setPreviewImage(profileData.prof_img);
    }
  };

  // إضافة وظيفة لإرسال كود الابن
  const handleAddChild = async () => {
    if (!childCode.trim()) {
      toast.error("يرجى إدخال كود الابن");
      return;
    }

    setIsAddingChild(true);
    
    try {
      const response = await fetch(`${BASE_URL}/parent/parent_get_child`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token3almny')}`
        },
        body: JSON.stringify({
          student_code: childCode.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("تم إضافة الابن بنجاح");
        setChildCode("");
        // يمكن إضافة تحديث للبيانات هنا إذا كان مطلوباً
      } else {
        toast.error(data.message || "حدث خطأ في إضافة الابن");
      }
    } catch (error) {
      console.error("Error adding child:", error);
      toast.error("حدث خطأ في الاتصال بالخادم");
    } finally {
      setIsAddingChild(false);
    }
  };

  if (profileLoading) {
    return (
      <div className={`profile-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner">جاري تحميل البيانات الشخصية...</div>
          </div>
        </div>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className={`profile-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="container">
          <div className="error-container">
            <div className="error-message">
              <p>حدث خطأ في تحميل البيانات الشخصية</p>
              <p>{profileError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`profile-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
      
      <div className="container">
        {/* Breadcrumb */}
        <div className="lessons-breadcrumb">
          <h1 className="breadcrumb-title">الملف الشخصي</h1>
          <div className="breadcrumb-links2">
            <span className="breadcrumb-link">الرئيسية</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">الملف الشخصي</span>
          </div>
        </div>

        <div className="profile-container">
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-image-container">
                <img 
                  src={previewImage || "/user.png"} 
                  alt="Profile" 
                  className="profile-image"
                  onError={(e) => {
                    e.target.src = "/user.png";
                  }}
                />
                {isEditing && (
                  <div className="image-upload">
                    <input
                      type="file"
                      id="profile-image"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="profile-image" className="upload-btn">
                      <FaEdit />
                    </label>
                  </div>
                )}
              </div>
              
              <div className="profile-actions">
                {!isEditing ? (
                  <>
                    <button 
                      className="edit-btn"
                      onClick={() => setIsEditing(true)}
                    >
                      <FaEdit />
                      تعديل البيانات
                    </button>
                    <button 
                      className="check-code-btn"
                      onClick={() => navigate('/check-code')}
                    >
                      <FaKey />
                      تفعيل كود المادة
                    </button>
                  </>
                ) : (
                  <div className="edit-actions">
                    <button 
                      className="save-btn"
                      onClick={handleSubmit}
                      disabled={updateProfileLoading}
                    >
                      <FaSave />
                      {updateProfileLoading ? "جاري الحفظ..." : "حفظ التغييرات"}
                    </button>
                    <button 
                      className="cancel-btn"
                      onClick={handleCancel}
                    >
                      <FaTimes />
                      إلغاء
                    </button>
                  </div>
                )}
              </div>
            </div>

            <form className="profile-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <FaUser />
                    الاسم
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="form-input"
                    placeholder="أدخل اسمك"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FaEnvelope />
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="form-input"
                    placeholder="أدخل بريدك الإلكتروني"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <FaPhone />
                    رقم الهاتف
                  </label>
                  <input
                    type="text"
                    value={profileData?.phone || ""}
                    disabled
                    className="form-input disabled"
                    placeholder="رقم الهاتف"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FaUser />
                    نوع الحساب
                  </label>
                  <input
                    type="text"
                    value={profileData?.role === 'student' ? 'طالب' : profileData?.role === 'teacher' ? 'معلم' : 'ولي أمر'}
                    disabled
                    className="form-input disabled"
                    placeholder="نوع الحساب"
                  />
                </div>
              </div>

              {profileData?.stud_code && profileData?.role !== 'teacher' && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <FaUser />
                      كود الطالب
                    </label>
                    <input
                      type="text"
                      value={profileData.stud_code}
                      disabled
                      className="form-input disabled"
                      placeholder="كود الطالب"
                    />
                  </div>
                </div>
              )}

              {isEditing && profileData?.role !== 'teacher' && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">المرحلة الدراسية</label>
                    <select
                      name="stage_id"
                      value={formData.stage_id}
                      onChange={handleInputChange}
                      className="form-select"
                      disabled={stagesLoading}
                    >
                      <option value="">اختر المرحلة</option>
                      {stages.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">الفصل الدراسي</label>
                    <select
                      name="class_id"
                      value={formData.class_id}
                      onChange={handleInputChange}
                      className="form-select"
                      disabled={classesLoading || !formData.stage_id}
                    >
                      <option value="">اختر الفصل</option>
                      {classes.map((classItem) => (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {!isEditing && (profileData?.stage_name || profileData?.class_name) && profileData?.role !== 'teacher' && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">المرحلة الدراسية</label>
                    <input
                      type="text"
                      value={profileData?.stage_name || ""}
                      disabled
                      className="form-input disabled"
                      placeholder="المرحلة الدراسية"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">الفصل الدراسي</label>
                    <input
                      type="text"
                      value={profileData?.class_name || ""}
                      disabled
                      className="form-input disabled"
                      placeholder="الفصل الدراسي"
                    />
                  </div>
                </div>
              )}

              {/* إضافة قسم كود الابن */}
              {profileData?.role === 'parent' && (
                <div className="child-code-section">
                  <h3 className="section-title">إضافة ابن جديد</h3>
                  <div className="form-row">
                    <div className="form-group flex-grow">
                      <label className="form-label">
                        <FaUser />
                        كود الابن
                      </label>
                      <input
                        type="text"
                        value={childCode}
                        onChange={(e) => setChildCode(e.target.value)}
                        className="form-input"
                        placeholder="أدخل كود الابن"
                        disabled={isAddingChild}
                      />
                    </div>
                    <div className="form-group button-group">
                      <button
                        type="button"
                        className="add-child-btn"
                        onClick={handleAddChild}
                        disabled={isAddingChild || !childCode.trim()}
                      >
                        <FaPlus />
                        {isAddingChild ? "جاري الإضافة..." : "إضافة الابن"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile; 